import {
  addDays,
  aisleOf,
  parseEntry,
  sameArticle,
  suggestIcon,
  type ListItem,
  type Atmosphere,
  type BringChangeInput,
  type FeedbackInput,
  type MagicAddInput,
  type MagicItem,
  type ReminderSettings,
  type Remind,
  taskInstanceId,
  instanceDate,
  type CalendarEvent,
  type CalendarSource,
  type CreateHouseholdInput,
  type Idea,
  type Locale,
  type Note,
  type Reward,
  type ShiftPlan,
  type Task,
  type Timetable,
  type Todo,
  type Voucher,
  type Wish,
  type WasteProvider,
  translateServer,
  type AiTask,
  type AiBudget,
  type AiTaskStats,
} from '@shh/shared';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  increment,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  type DocumentReference,
} from 'firebase/firestore';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  signOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { firebase } from '../lib/firebase';
import { clearInvite, readInvite } from '../lib/invite';
import type { FamilyData } from './family';
import { refreshClaims } from './session';
import { appText } from '../i18n/app';
import { locale } from '../lib/locale';

/**
 * Everything the app changes. Plain Firestore writes where the rules allow it (they work
 * offline and sync later), callables where the server must decide (stars, PINs, pairing).
 */
const fns = () => firebase().functions;
const db = () => firebase().db;
/** Callables send `undefined` as `null`, which the server's optional fields reject – drop them. */
const stripUndefined = (v: unknown): unknown =>
  Array.isArray(v)
    ? v.map(stripUndefined)
    : v && typeof v === 'object'
      ? Object.fromEntries(
          Object.entries(v)
            .filter(([, x]) => x !== undefined)
            .map(([k, x]) => [k, stripUndefined(x)]),
        )
      : v;
/**
 * The AI callables may take up to 75 s on the server (lib/gemini.ts BUDGET_MS) – the app waits a
 * little longer than that instead of the SDK's 70 s, so it never gives up while the answer comes.
 */
const AI_CALLS = new Set(['magicAdd']);
const call = async <T = unknown>(name: string, data?: unknown): Promise<T> =>
  (
    await httpsCallable(
      fns(),
      name,
      AI_CALLS.has(name) ? { timeout: 85_000 } : undefined,
    )(stripUndefined(data))
  ).data as T;
const ref = (hid: string, path: string) => doc(db(), `households/${hid}/${path}`);
const colRef = (hid: string, name: string) => collection(db(), `households/${hid}/${name}`);

/**
 * What to show when a server call fails. Our functions answer with German sentences meant for
 * people; anything else ("internal", network trouble, a function not yet released) gets a plain
 * sentence instead of a technical code.
 */
export function friendlyError(e: unknown): string {
  const code = (e as { code?: unknown })?.code;
  const ours = [
    'functions/invalid-argument',
    'functions/failed-precondition',
    'functions/unavailable',
    'functions/permission-denied',
    'functions/already-exists',
    'functions/unauthenticated',
    'functions/not-found',
    'functions/resource-exhausted',
  ];
  if (typeof code === 'string' && ours.includes(code) && e instanceof Error && e.message) {
    // the Firebase SDK appends the HTTP status ("… [401]") – people need only our sentence
    // the server speaks German – an English device gets the translation (1.19)
    return translateServer(e.message.replace(/\s*\[\d{3}\]$/, ''), locale);
  }
  return appText.failedLater;
}

/** Remove undefined values – Firestore rejects them. */
const clean = <T extends object>(o: T) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;

// ---------- household & devices ----------
export async function createHousehold(
  input: Omit<CreateHouseholdInput, 'demoAs' | 'invite'> &
    Partial<Pick<CreateHouseholdInput, 'demoAs'>>,
) {
  // a test family invitation (1.17) belongs to the real family, never to the demo
  const invite = input.demo ? undefined : readInvite();
  const r = await call<{ householdId: string; testFamily?: boolean }>('createHousehold', {
    ...input,
    invite,
  });
  if (invite) clearInvite();
  await refreshClaims();
  return r;
}

// ---------- Testfamilien (1.17): Mike's /admin ----------
export interface WaitlistEntry {
  id: string;
  email: string;
  source: string;
  confirmedAt: number;
  invitedAt: number | null;
  joinedAt: number | null;
}
export const adminWaitlist = () =>
  call<{ unconfirmed: number; entries: WaitlistEntry[] }>('adminWaitlist');
export const adminInvite = (ids: string[]) => call<{ invited: string[] }>('adminInvite', { ids });

/** Admin overview (after 1.18): counts and days only – see functions/src/lib/adminOverview.ts. */
export interface AdminOverview {
  numbers: {
    families: number;
    testFamilies: number;
    active7: number;
    active30: number;
    accounts: number;
    calendars: number;
    waste: number;
    walls: number;
    wallFamilies: number;
    walls7: number;
    push: number;
    aiEnabled: number;
    aiToday: number;
    feedback: number;
    feedback30: Record<string, number>;
  };
  funnel: {
    unconfirmed: number;
    confirmed: number;
    invited: number;
    joined: number;
    bySource: Record<string, number>;
    byLocale: Record<string, number>;
  };
  families: {
    id: string;
    name: string;
    created: string | null;
    testFamily: boolean;
    /** Plus switched on by hand (1.21) */
    plus: boolean;
    /** Plus switched off by hand – no trial (1.21) */
    free: boolean;
    parents: number;
    teens: number;
    kids: number;
    walls: number;
    calendars: number;
    waste: number;
    push: number;
    ai: boolean;
    lastActive: string | null;
  }[];
  accounts: {
    email: string;
    provider: string;
    verified: boolean;
    created: string | null;
    lastActive: string | null;
    family: string | null;
    role: 'parent' | 'teen' | null;
    familyId: string | null;
    testFamily: boolean;
  }[];
}
export const adminOverview = () => call<AdminOverview>('adminOverview');
/** The AI tab (1.27): measuring per job, and one test against one model. */
export interface AiDayRow {
  day: string;
  cents: number;
  limitCents: number;
  families: number;
  calls: number;
  blockedApp: number;
  blockedFamily: number;
  familiesAtLimit: number;
}
export const adminAiStats = () =>
  call<{ week: AiTaskStats[]; month: AiTaskStats[]; budget?: AiBudget; days?: AiDayRow[] }>(
    'adminAiStats',
  );
export const adminSetAiBudget = (b: AiBudget) => call<AiBudget>('adminSetAiBudget', b);
export interface AiTestResult {
  model: string;
  ok: boolean;
  error?: string;
  items: Record<string, unknown>[];
  ms: number;
  attempts: number;
  tokensIn: number;
  tokensOut: number;
  costCents: number;
}
export const adminAiTest = (input: {
  task: AiTask;
  model: string;
  text?: string;
  image?: { mimeType: 'image/jpeg' | 'image/png' | 'image/webp'; data: string };
}) =>
  httpsCallable(fns(), 'adminAiTest', { timeout: 95_000 })(stripUndefined(input)).then(
    (r) => r.data as AiTestResult,
  );
/** Plus on/off for one family (1.21) – testing and exceptions until payment exists. */
export const adminSetPlan = (familyId: string, plan: 'plus' | 'free' | 'testfamily' | null) =>
  call<{ plan: string | null }>('adminSetPlan', { familyId, plan });
/**
 * DSGVO: all of the family's data as a JSON file (parents' own devices only). The browser saves
 * it – nothing leaves the device except the download itself.
 */
export async function downloadExport(familyName: string, today: string) {
  const data = await call('exportHousehold');
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const slug = familyName
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/g, '-')
    .replace(/^-|-$/g, '');
  a.download = `sweethomehub-${slug || 'familie'}-${today}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
/** Deletes the family for everyone; afterwards this account is back at "Wie möchtest du starten?". */
export async function deleteHousehold(householdId: string) {
  await call('deleteHousehold', { householdId });
  await refreshClaims();
}
/** Set once "Konto löschen" worked – the login page says goodbye instead of greeting. */
let deletedJustNow = false;
export const takeGoodbye = () => {
  const was = deletedJustNow;
  deletedJustNow = false;
  return was;
};
/**
 * "Konto löschen" (1.17): the server wants a fresh sign-in, so confirm the password (or Google)
 * first. Afterwards the account no longer exists – sign out locally as well.
 */
export async function deleteAccount(password?: string) {
  const { auth } = firebase();
  const user = auth.currentUser;
  if (!user) return;
  if (user.providerData.some((p) => p.providerId === 'password') && user.email) {
    await reauthenticateWithCredential(
      user,
      EmailAuthProvider.credential(user.email, password ?? ''),
    );
  } else {
    await reauthenticateWithPopup(user, new GoogleAuthProvider());
  }
  await call('deleteAccount', { locale }); // the goodbye mail in this device's language
  deletedJustNow = true;
  await signOut(auth).catch(() => undefined);
}
/**
 * "E-Mail ändern" (1.17): confirm with the password first (the server wants a fresh sign-in),
 * then the server mails the new address a link and tells the old one.
 */
export async function requestEmailChange(email: string, password: string) {
  const user = firebase().auth.currentUser;
  if (!user?.email) return;
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
  await call('requestEmailChange', { email, origin: window.location.origin, locale });
}
export async function acceptInvite(code: string) {
  const r = await call('acceptInvite', { code });
  await refreshClaims();
  return r;
}
export const createInvite = (memberId: string) =>
  call<{ code: string; expiresAt: number }>('createInvite', { memberId });
export const createPairingCode = (name?: string) =>
  call<{ code: string; expiresAt: number }>('createPairingCode', { name });
export async function pairDevice(code: string, name = 'Wanddisplay') {
  const r = await call('pairDevice', { code, name });
  await refreshClaims();
  return r;
}
export const unpairDevice = (deviceUid: string) => call('unpairDevice', { deviceUid });
export const renameDevice = (deviceUid: string, name: string) =>
  call('renameDevice', { deviceUid, name });
export const deviceSeen = () => call<{ paired: boolean }>('deviceSeen');
export const verifyParentPin = (pin: string, memberId?: string) =>
  call<{ ok: boolean; triesLeft?: number; expiresAt?: number }>('verifyParentPin', {
    pin,
    memberId,
  });
export const endParentSession = () => call('endParentSession');
export const extendParentSession = () =>
  call<{ ok: boolean; expiresAt: number }>('extendParentSession');
export const changeParentPin = (pin: string) => call('changeParentPin', { pin });

// ---------- tasks ----------
/**
 * Tick or untick. The first tick creates today's instance ("wer zuerst kommt"); the server
 * books the stars and turns "done" into "pending" where parents must confirm.
 */
export async function toggleTask(
  f: FamilyData,
  task: Pick<Task, 'requiresApproval' | 'repeat'> & { id: string },
  memberId: string,
) {
  // a flexible task ("1× pro Woche") keeps one state for its whole period
  const day = instanceDate(task.repeat, f.today);
  const r = ref(f.householdId, `taskInstances/${taskInstanceId(task.id, day)}`);
  const existing = f.instances[task.id];
  const ticked = task.requiresApproval ? 'pending' : 'done';
  if (!existing) {
    await setDoc(r, {
      taskId: task.id,
      date: day,
      memberId,
      status: ticked,
      doneAt: Date.now(),
    });
  } else if (existing.status === 'open') {
    // ticked alone this time – helpers of an earlier tick do not share again
    await updateDoc(r, { status: ticked, memberId, doneAt: Date.now(), with: [] });
  } else if (existing.status !== 'approved') {
    await updateDoc(r, { status: 'open' });
  }
}
/**
 * "Zusammen gemacht" (1.9): several people did it – each gets a share of the stars, rounded up
 * (the server books them). The first one in the list counts as the one who ticked.
 */
export async function tickTogether(
  f: FamilyData,
  task: Pick<Task, 'requiresApproval' | 'repeat'> & { id: string },
  memberIds: string[],
) {
  const [first, ...rest] = memberIds;
  if (!first) return;
  // a flexible task ("1× pro Woche") keeps one state for its whole period
  const day = instanceDate(task.repeat, f.today);
  const r = ref(f.householdId, `taskInstances/${taskInstanceId(task.id, day)}`);
  const existing = f.instances[task.id];
  const ticked = task.requiresApproval ? 'pending' : 'done';
  if (!existing) {
    await setDoc(r, {
      taskId: task.id,
      date: day,
      memberId: first,
      status: ticked,
      doneAt: Date.now(),
      with: rest,
    });
  } else if (existing.status === 'open') {
    await updateDoc(r, { status: ticked, memberId: first, doneAt: Date.now(), with: rest });
  } else if (existing.status !== 'approved') {
    // already ticked: only who helped changes
    await updateDoc(r, { with: memberIds.filter((id) => id !== existing.memberId) });
  }
}
export const approveTask = (instanceId: string) => call('approveTask', { instanceId });
export const returnTask = (instanceId: string, note?: string) =>
  call('returnTask', { instanceId, note });

export async function saveTask(
  hid: string,
  task: Omit<Task, 'id' | 'source' | 'active'> & { id?: string },
) {
  const { id, ...data } = task;
  const body = clean({ ...data, icon: data.icon || suggestIcon(data.title) });
  // editing keeps origin and on/off state (starter routines stay "template")
  if (id) await setDoc(ref(hid, `tasks/${id}`), body, { merge: true });
  else
    await addDoc(colRef(hid, 'tasks'), {
      ...body,
      active: true,
      source: 'manual' as const,
      createdAt: Date.now(),
    });
}
export const deleteTask = (hid: string, id: string) => deleteDoc(ref(hid, `tasks/${id}`));

// ---------- to-dos ----------
// ---------- pinboard ----------
/** Pin a new note, or fix an existing one (text and "bis" only – the author stays). */
export async function saveNote(
  hid: string,
  note: Pick<Note, 'text' | 'until' | 'createdBy'> & { id?: string },
) {
  if (note.id) {
    await updateDoc(ref(hid, 'notes/' + note.id), {
      text: note.text,
      until: note.until ?? deleteField(),
    });
  } else {
    await addDoc(
      colRef(hid, 'notes'),
      clean({
        text: note.text,
        until: note.until,
        createdBy: note.createdBy,
        createdAt: Date.now(),
      }),
    );
  }
}
export const deleteNote = (hid: string, id: string) => deleteDoc(ref(hid, 'notes/' + id));

export async function saveTodo(hid: string, todo: Omit<Todo, 'id' | 'done'> & { id?: string }) {
  const { id, ...data } = todo;
  const body = clean({ ...data, icon: data.icon || suggestIcon(data.title) });
  if (id) await updateDoc(ref(hid, `todos/${id}`), body);
  else await addDoc(colRef(hid, 'todos'), { ...body, done: false, createdAt: Date.now() });
}
export async function toggleTodo(
  hid: string,
  todo: Pick<Todo, 'done'> & { id: string },
  memberId?: string,
) {
  await updateDoc(
    ref(hid, `todos/${todo.id}`),
    todo.done
      ? { done: false, doneBy: null, doneAt: null }
      : clean({ done: true, doneBy: memberId ?? null, doneAt: Date.now() }),
  );
}
export const deleteTodo = (hid: string, id: string) => deleteDoc(ref(hid, `todos/${id}`));

// ---------- calendar ----------
export async function saveEvent(
  hid: string,
  e: Omit<CalendarEvent, 'id' | 'source'> & { id?: string },
) {
  const { id, ...data } = e;
  const body = clean({
    ...data,
    icon: data.icon || suggestIcon(data.title, 'calendar'),
    source: 'manual' as const,
  });
  if (id) await setDoc(ref(hid, `events/${id}`), body);
  else await addDoc(colRef(hid, 'events'), body);
}
export const deleteEvent = (hid: string, id: string) => deleteDoc(ref(hid, `events/${id}`));
/**
 * Ticks an event off for one day (1.25, Mike 20.10.) – like a task: the first one ticks, the
 * server books the stars. Unticking keeps the document so the server can take the stars back.
 */
export async function toggleEventCheck(
  f: FamilyData,
  eventId: string,
  date: string,
  memberId: string,
) {
  const r = ref(f.householdId, `eventChecks/${eventId}_${date}`);
  const existing = f.eventChecks[`${eventId}_${date}`];
  if (!existing) await setDoc(r, { eventId, date, memberId, status: 'done', doneAt: Date.now() });
  else if (existing.status === 'done') await updateDoc(r, { status: 'open' });
  else await updateDoc(r, { status: 'done', doneAt: Date.now() });
}
/** Shift plans (1.25). */
export async function saveShiftPlan(hid: string, p: Omit<ShiftPlan, 'id'> & { id?: string }) {
  const { id, ...data } = p;
  const body = clean(data);
  if (id) await setDoc(ref(hid, `shiftPlans/${id}`), body);
  else await addDoc(colRef(hid, 'shiftPlans'), body);
}
/** Only on this day: another shift, free ("-"), holiday ("U") – or back to the rhythm (null). */
export const setShiftDay = (hid: string, id: string, date: string, key: string | null) =>
  updateDoc(ref(hid, `shiftPlans/${id}`), { [`over.${date}`]: key ?? deleteField() });
/** Settings (1.28, Mike 27.10.: "sollte bei Schichtplan auch möglich sein"): the whole plan goes. */
export const deleteShiftPlan = (hid: string, id: string) => deleteDoc(ref(hid, `shiftPlans/${id}`));
/** The plan stops: its last day is the day before. */
export const endShiftPlan = (hid: string, id: string, date: string) =>
  updateDoc(ref(hid, `shiftPlans/${id}`), { until: addDays(date, -1) });

/** "Diesen und alle folgenden" (1.25): the old series ends the day before. */
export const endSeriesBefore = (hid: string, id: string, date: string) =>
  updateDoc(ref(hid, `events/${id}`), { 'repeat.until': addDays(date, -1) });
/** Takes one date out of a series – the rest stays (Mike, 19.10.). */
export const skipOccurrence = (hid: string, id: string, date: string) =>
  updateDoc(ref(hid, `events/${id}`), { 'repeat.skip': arrayUnion(date) });

export async function saveSource(hid: string, s: Omit<CalendarSource, 'id'> & { id?: string }) {
  const { id, ...data } = s;
  const body = clean(data);
  let r: DocumentReference;
  if (id) {
    r = ref(hid, `calendarSources/${id}`);
    await setDoc(r, body);
  } else r = await addDoc(colRef(hid, 'calendarSources'), body);
  return r.id;
}
/** "Erinnern" for every event of a calendar (1.13); undefined = the family default. */
export const setSourceRemind = (hid: string, id: string, remind: Remind | undefined) =>
  updateDoc(ref(hid, `calendarSources/${id}`), { remind: remind ?? deleteField() });
export const deleteSource = (hid: string, id: string) =>
  deleteDoc(ref(hid, `calendarSources/${id}`));
export const syncCalendar = (sourceId: string) =>
  call<{ events: number; tasks: number }>('syncCalendar', { sourceId });
/** Calendar assistant: what is behind a pasted link? */
export const previewCalendar = (url: string) =>
  call<{
    count: number;
    examples: { title: string; date: string; start?: string }[];
    /** W2: looks like a waste calendar, and its bins (older servers send neither). */
    waste?: boolean;
    bins?: string[];
  }>('previewCalendar', { url });
/** The family's own dates as a subscription link for the phones (renew = old link stops). */
export const calendarFeedLink = (renew = false) =>
  call<{ url: string }>('calendarFeedLink', { renew });

/** A town at one waste company (Wohnort → Entsorger, 1.15). */
export interface WastePlace {
  provider: WasteProvider;
  service: string;
  placeId: string;
  place: string;
  company: string;
}
export interface WasteStreet {
  id: string;
  name: string;
  addressId: string;
}
type At = Pick<WastePlace, 'provider' | 'service' | 'placeId'>;
const at = (p: At) => ({ provider: p.provider, service: p.service, placeId: p.placeId });
export const wasteFind = (place: string) =>
  call<WastePlace[]>('wasteLookup', { action: 'find', place });
export const wasteStreets = (p: At, query: string) =>
  call<{ streets: WasteStreet[]; placeAddressId: string | null }>('wasteLookup', {
    action: 'streets',
    ...at(p),
    query,
  });
export const wasteHouseNumbers = (p: At, streetId: string) =>
  call<{ number: string; addressId: string }[]>('wasteLookup', {
    action: 'houseNumbers',
    ...at(p),
    streetId,
  });
export const wasteTypes = (p: At, addressId: string) =>
  call<{ name: string; title: string }[]>('wasteLookup', { action: 'types', ...at(p), addressId });

export const saveTimetable = (hid: string, tt: Timetable) =>
  setDoc(ref(hid, `timetables/${tt.memberId}`), tt);
export const deleteTimetable = (hid: string, memberId: string) =>
  deleteDoc(ref(hid, `timetables/${memberId}`));

// ---------- stars ----------
export async function requestReward(
  hid: string,
  reward: Pick<Reward, 'cost'> & { id: string },
  memberId: string,
) {
  await addDoc(colRef(hid, 'redemptions'), {
    rewardId: reward.id,
    memberId,
    cost: reward.cost,
    status: 'requested',
    createdAt: Date.now(),
  });
}
export const cancelRequest = (hid: string, id: string) => deleteDoc(ref(hid, `redemptions/${id}`));
export const decideRedemption = (redemptionId: string, approve: boolean) =>
  call('decideRedemption', { redemptionId, approve });
export const giveKudos = (memberId: string, text: string, points: number) =>
  call('giveKudos', { memberId, text, points });

export async function saveReward(hid: string, r: Omit<Reward, 'id' | 'active'> & { id?: string }) {
  const { id, ...data } = r;
  if (id) await setDoc(ref(hid, `rewards/${id}`), { ...data, active: true });
  else await addDoc(colRef(hid, 'rewards'), { ...data, active: true });
}
export const deleteReward = (hid: string, id: string) => deleteDoc(ref(hid, `rewards/${id}`));

export async function saveGoal(hid: string, title: string, target: number) {
  const r = ref(hid, 'goals/family');
  const exists = (await getDoc(r)).exists();
  if (exists) await updateDoc(r, { title, target });
  else await setDoc(r, { title, target, progress: 0 });
}

// ---------- members & household ----------
export const updateMember = (
  hid: string,
  id: string,
  patch: Partial<{
    name: string;
    color: string;
    emoji: string | null;
    ageProfile: string;
    goalRewardId: string;
    archived: boolean;
    /** "MM-DD"; null removes it */
    birthday: string | null;
  }>,
) =>
  updateDoc(
    ref(hid, `members/${id}`),
    clean({ ...patch, birthday: patch.birthday === null ? deleteField() : patch.birthday }),
  );

export const updateHousehold = (
  hid: string,
  patch: Partial<{
    name: string;
    location: object;
    locale: Locale;
    daylight: 'off' | 'subtle' | 'strong';
    atmosphere: Atmosphere;
    ai: { enabled: boolean };
    reminders: ReminderSettings;
    festive: { enabled: boolean; off: string[] };
  }>,
) => updateDoc(doc(db(), `households/${hid}`), clean(patch));

// ---------- Freizeit (1.10) ----------
type IdeaInput = Pick<Idea, 'title' | 'place' | 'length' | 'cost' | 'note' | 'spot'> & {
  id?: string;
};
/**
 * New idea or fix an existing one. Whoever adds it wants to go – the first heart is theirs
 * (on the wall: the person picked at the top; the author stays empty there, like pinned notes).
 */
export async function saveIdea(
  hid: string,
  idea: IdeaInput,
  who: { createdBy?: string; heart?: string } = {},
) {
  const { id, ...data } = idea;
  if (id) {
    await updateDoc(ref(hid, `ideas/${id}`), {
      title: data.title,
      place: data.place,
      length: data.length ?? deleteField(),
      cost: data.cost ?? deleteField(),
      note: data.note ?? deleteField(),
      spot: data.spot ?? deleteField(),
    });
  } else {
    await addDoc(
      colRef(hid, 'ideas'),
      clean({
        ...data,
        hearts: who.heart ? [who.heart] : [],
        createdBy: who.createdBy,
        createdAt: Date.now(),
      }),
    );
  }
}
export const toggleHeart = (hid: string, ideaId: string, memberId: string, on: boolean) =>
  updateDoc(ref(hid, `ideas/${ideaId}`), {
    hearts: on ? arrayUnion(memberId) : arrayRemove(memberId),
  });
/** "Einplanen": the calendar entry and the mark on the idea; null = back on the list. */
export const markIdeaPlanned = (hid: string, ideaId: string, day: string | null) =>
  updateDoc(ref(hid, `ideas/${ideaId}`), { planned: day ?? deleteField() });
export const deleteIdea = (hid: string, id: string) => deleteDoc(ref(hid, `ideas/${id}`));

type VoucherInput = Pick<
  Voucher,
  'title' | 'value' | 'until' | 'code' | 'place' | 'from' | 'forWhom' | 'note'
> & { id?: string };
export async function saveVoucher(hid: string, v: VoucherInput, createdBy?: string) {
  const { id, ...data } = v;
  if (id) {
    const optional = ['value', 'until', 'code', 'place', 'from', 'note'] as const;
    await updateDoc(ref(hid, `vouchers/${id}`), {
      title: data.title,
      forWhom: data.forWhom,
      ...Object.fromEntries(optional.map((k) => [k, data[k] ?? deleteField()])),
    });
  } else {
    await addDoc(
      colRef(hid, 'vouchers'),
      clean({ ...data, used: false, createdBy, createdAt: Date.now() }),
    );
  }
}
export const setVoucherUsed = (hid: string, id: string, used: boolean) =>
  updateDoc(
    ref(hid, `vouchers/${id}`),
    used ? { used, usedAt: Date.now() } : { used, usedAt: deleteField() },
  );
export const setVoucherQuiet = (hid: string, id: string, quiet: boolean) =>
  updateDoc(ref(hid, `vouchers/${id}`), { quiet });
export const deleteVoucher = (hid: string, id: string) => deleteDoc(ref(hid, `vouchers/${id}`));

type WishInput = Pick<Wish, 'owner' | 'title' | 'price' | 'link' | 'love' | 'occasion'> & {
  id?: string;
};
export async function saveWish(hid: string, w: WishInput, createdBy?: string) {
  const { id, owner, ...data } = w;
  if (id) {
    await updateDoc(ref(hid, `wishes/${id}`), {
      title: data.title,
      love: data.love,
      occasion: data.occasion,
      price: data.price ?? deleteField(),
      link: data.link ?? deleteField(),
    });
  } else {
    await addDoc(
      colRef(hid, 'wishes'),
      clean({ ...data, owner, createdBy, createdAt: Date.now() }),
    );
  }
}
export const setWishReceived = (hid: string, id: string, received: boolean) =>
  updateDoc(ref(hid, `wishes/${id}`), { received });
export async function deleteWish(hid: string, id: string, adult: boolean) {
  await deleteDoc(ref(hid, `wishes/${id}`));
  // the secret mark goes with it (only adults may touch it – and only they ever set one)
  if (adult) await deleteDoc(ref(hid, `wishClaims/${id}`)).catch(() => undefined);
}
/** "Ich besorge das" / "Zusammen schenken"; an empty list takes the mark away. */
export const claimWish = (hid: string, wishId: string, by: string[]) =>
  by.length
    ? setDoc(ref(hid, `wishClaims/${wishId}`), { by, createdAt: Date.now() })
    : deleteDoc(ref(hid, `wishClaims/${wishId}`));

// ---------- KI-Helfer ("Einfach sagen") ----------
// Account mails (Resend). The page's own address goes along, so links in previews lead back
// there – the server only accepts our own addresses.
export const sendResetMail = (email: string) =>
  call('sendResetMail', { email, origin: window.location.origin, locale });
export const sendVerificationMail = () =>
  call('sendVerificationMail', { origin: window.location.origin, locale });
export const magicAdd = (input: MagicAddInput) => call<{ items: MagicItem[] }>('magicAdd', input);

// ---------- Ideen & Feedback (straight to us, the server keeps it away from everybody else) ----------
export const sendFeedback = (input: FeedbackInput) => call('sendFeedback', input);

// ---------- Bring! shopping list (the server talks to Bring!, the app reads the mirror) ----------
export const bringConnect = (email: string, password: string) =>
  call<{ listName: string; count: number }>('bringConnect', { email, password });
export const bringChooseList = (listUuid: string) => call('bringChooseList', { listUuid });
export const bringDisconnect = () => call('bringDisconnect');
export const bringSync = () => call('bringSync');
export const bringChange = (change: BringChangeInput) => call('bringChange', change);

// ---------- own shopping list (1.20): plain writes, so ticking off works offline in the shop ----------
type StoredItem = ListItem & { id: string };
const itemRef = (hid: string, id: string) => ref(hid, `shoppingItems/${id}`);

/**
 * Add what is missing. Never twice: an open item only gets the new amount, one under "recently
 * bought" is reopened – its aisle stays, so a move by the family is remembered.
 * Callers do not wait for the promise: offline it only settles once the shop has signal again.
 */
export function addToList(
  hid: string,
  items: StoredItem[],
  input: string | { name: string; spec: string },
  addedBy: string | null = null,
) {
  // typed into the field ("2 Liter Milch") or already split (AI helper)
  const { name, spec } = typeof input === 'string' ? parseEntry(input) : input;
  if (!name) return Promise.resolve();
  const same = items.find((i) => sameArticle(i.name, name));
  if (same) {
    return updateDoc(itemRef(hid, same.id), {
      checked: false,
      checkedAt: null,
      // keep the old amount when nothing new was said for an open item
      ...(spec || same.checked ? { spec } : {}),
    });
  }
  const item: ListItem = {
    name,
    spec,
    aisle: aisleOf(name),
    checked: false,
    checkedAt: null,
    addedBy,
    createdAt: Date.now(),
    count: 0,
  };
  return setDoc(doc(colRef(hid, 'shoppingItems')), item);
}
/**
 * Ticking off counts a purchase – "Oft gekauft" and the suggestions sort by it. Putting it back
 * on the list means "buy again", so the count stays.
 */
export const tickItem = (hid: string, id: string, checked: boolean) =>
  updateDoc(itemRef(hid, id), {
    checked,
    checkedAt: checked ? Date.now() : null,
    ...(checked ? { count: increment(1) } : {}),
  });
export const changeItem = (
  hid: string,
  id: string,
  patch: Partial<Pick<ListItem, 'name' | 'spec' | 'aisle'>>,
) => updateDoc(itemRef(hid, id), patch);
export const removeItem = (hid: string, id: string) => deleteDoc(itemRef(hid, id));

/** Kid ↔ teen ↔ little one: the role decides permissions, so the server changes it. */
export const changeMemberKind = (memberId: string, kind: 'teen' | 'kid' | 'little') =>
  call('changeMemberKind', { memberId, kind });
export const addMember = (m: {
  name: string;
  role: 'parent' | 'teen' | 'kid';
  ageProfile: 'picture' | 'kid' | 'teen';
  color: string;
  emoji?: string;
  withRoutines?: boolean;
}) => call<{ memberId: string }>('addMember', clean(m));
